import {
  hasPracticeStep,
  hasShapeStep,
  phraseHandShapePlan,
  shapeStepCount,
} from './hand-shapes';
import type {
  Phrase,
  PhraseEvent,
  PhraseFSMSnapshot,
  PhraseState,
  RunHistory,
  RunHistoryEntry,
  RunOutcome,
} from './types';

/**
 * Finite-state machine for a single phrase.
 *
 * S0 → S1a → S1b → S2a → S2b → S3 → S4 → S5 → DONE.
 *
 * Skipping rules (driven by `phrase.notesByHand`):
 *
 * - No left-hand notes  → S1a and S1b are skipped.
 * - No right-hand notes → S2a and S2b are skipped.
 * - Only one hand has notes → S3 (both-hands practice) is also skipped.
 * - No notes at all → the phrase resolves to DONE right after S0.
 *
 * ## S4 and S5 count nothing
 *
 * They used to. A rhythm state validated on **two consecutive clean runs**,
 * a clean run tolerating at most two errors and **no** missed note, and three
 * failures in `both` demoted the unit to one hand. Replayed over the real
 * work units (PDMX testset 200, 10 528 units), that gate killed 100% of
 * passes on a single forgotten note, failed 15.0% of units at ordinary human
 * jitter — 98.5% of those with no timing error at all, pure artefacts of
 * attributing a press by pitch — and passed 62.2% of units played with no
 * rhythm whatsoever. It is gone, with its counters.
 *
 * What remains is the shape the no-rhythm states already use: the run either
 * happened or the player stopped, the view decides which and says so, and
 * **advancing is the player's deliberate act**. A `validated` outcome here
 * means "the player pressed Continue", not "the machine graded a pass".
 *
 * ## S4 has no ladder of hands any more
 *
 * It used to walk `lh-only → rh-only → both`, one validated pass per rung, so
 * a two-hand unit was played in time three times over. Those two mono rungs
 * were a decomposition offered **after** the parcours had already decomposed
 * the unit: S1b is the left hand alone, S2b the right hand alone, S3 the two
 * together. Meeting the passage in time then re-ran the same three-part
 * exercise, at the cost of two more passes of a step that only teaches one
 * thing — placing the hands in time.
 *
 * What replaces them is a continuity: S3 measures how the player is actually
 * playing, and hands S4 the tempo it read. See `pace`, and `mastery.ts` for
 * the arithmetic. There is exactly one run in time, at the player's own
 * speed, and the speed is what climbs.
 *
 * **Reload.** `pace` is not persisted: it is measured by the practice run
 * that precedes the run in time, so a fresh FSM has nothing to hand over and
 * S4 falls back to the density rule.
 *
 * ## S3 and S4 are one step in two régimes
 *
 * The stepper has always folded them into one segment — « les deux mains »,
 * with or without the metronome — and the graph did not: `S3` handed over to
 * `S4` on a validated run and there was no way back but « retour ». So the
 * player crossed a one-way door they had not asked to open, at a moment
 * chosen for them.
 *
 * They are now **one step the player switches between**. `enterTime` and
 * `leaveTime` are the switch, both directions, as often as wanted, and they
 * are the *only* door: a validated run on the free régime records the pass
 * and stays put. Nothing here reads a number and promotes.
 *
 * The free régime of the step is not always `S3`. On a unit where only one
 * hand plays, the run without rhythm is that hand's own (`S1b` / `S2b`) and
 * `S3` does not exist; on a unit whose two hands strike one instant together
 * there is no both-hands run either. `freeRegimeState` names whichever it is,
 * and the switch is drawn from it in both directions — so the round trip
 * lands where the player was, on every shape of unit.
 *
 * **The tempo survives the round trip.** `pace` is deliberately *not* cleared
 * by `leaveTime`: the metronome is a régime of the step, not a rung that has
 * been climbed, and coming back to it at a tempo the player never held would
 * make leaving it cost something. Only a jump from the stepper drops it, and
 * that one is a jump, not a switch — see `fastForwardTo`.
 *
 * ## Going back is one sub-step, not one segment
 *
 * `stepBack` walks the states, and the stepper's five segments fold nine of
 * them. Going back from the two-hand practice used to land on the right
 * hand's *grip* — two sub-steps behind — and everything between had to be
 * played again to return. See `previousState`.
 *
 * No React, Studio or DOM dependency, pure TS, testable from Jest in
 * `node` environment.
 */
/**
 * Canonical ordering of phrase states used to derive `maxState`. `DONE`
 * is the absolute terminal state; everything before it follows the
 * S0→S5 progression.
 */
const STATE_ORDER: readonly PhraseState[] = [
  'S0',
  'S1a',
  'S1b',
  'S2a',
  'S2b',
  'S3',
  'S4',
  'S5',
  'DONE',
];

function stateIndex(state: PhraseState): number {
  return STATE_ORDER.indexOf(state);
}

function maxOfStates(a: PhraseState, b: PhraseState): PhraseState {
  return stateIndex(a) >= stateIndex(b) ? a : b;
}

export class PhraseFSM {
  private state: PhraseState = 'S0';
  private maxState: PhraseState = 'S0';
  private positionIndex = 0;
  /**
   * The tempo the run in time will open at, as a fraction of the written
   * one, or `null` when nothing has measured one yet. Handed over by the
   * two-hand practice; see the note on the class.
   */
  private pace: number | null = null;
  private runHistory: RunHistoryEntry[] = [];

  private runIndex = 0;

  private listeners = new Set<(snapshot: PhraseFSMSnapshot) => void>();
  private cachedSnapshot: PhraseFSMSnapshot | null = null;

  /**
   * `chains` is `false` for the **first unit of the piece**, which has
   * nothing before it to be joined to.
   *
   * The chaining step exists to play the seam between two units that are
   * each already fluent. On the first one there is no seam: the step fell
   * back to replaying the unit whole, which is what the last rung of the
   * step before it just did. A duplicate presented as the thing that
   * finishes the unit — and, until the reading was fixed, one the player
   * could get stuck on for a passage they had already played through.
   */
  constructor(
    private readonly phrase: Phrase,
    opts: { readonly chains?: boolean } = {},
  ) {
    this.chains = opts.chains ?? true;
  }

  private readonly chains: boolean;

  /**
   * Returns the current snapshot. Two consecutive calls without any
   * `dispatch` in between return the *same reference*, which lets
   * React consumers `useSyncExternalStore` against it without spurious
   * re-renders.
   */
  getSnapshot(): PhraseFSMSnapshot {
    if (this.cachedSnapshot === null) {
      this.cachedSnapshot = this.buildSnapshot();
    }

    return this.cachedSnapshot;
  }

  /**
   * Subscribes to state changes. The listener is invoked once per
   * effective mutation (no spurious calls when an event is ignored).
   * Returns an unsubscribe function.
   */
  subscribe(listener: (snapshot: PhraseFSMSnapshot) => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Fast-forwards the FSM directly to `target`, bypassing the regular
   * event flow. **Only** intended for hydration from a persisted progress
   * snapshot, never call this from runtime code, it would break the
   * invariants the dispatcher relies on (run streak counters, mode
   * advance state, etc.).
   *
   * Targets above `S0`/`DONE` skip the corresponding intermediate states.
   * The position index, run history, measured tempo and run lifecycle
   * counters are reset to their default, we are reconstructing a clean
   * state at `target`, not replaying the whole journey.
   *
   * Going *backwards* (target earlier than the current state) is allowed
   * and intentional: the orchestrator may want to reset a phrase. Going
   * to a state the phrase cannot reach (e.g. `S1a` on a RH-only phrase)
   * is silently coerced to the closest legal state in the same direction.
   */
  fastForwardTo(target: PhraseState): void {
    const legal = this.coerceToLegalState(target);

    if (legal === this.state) {
      return;
    }

    this.state = legal;
    this.maxState = maxOfStates(this.maxState, legal);
    this.positionIndex = 0;
    // Dropped rather than carried: a tempo is measured on a pass that just
    // happened, and a jump anywhere from the stepper means the pass before it
    // is not the pass before it any more. The run in time then opens on what
    // the unit is remembered by, which is what a return should find.
    this.pace = null;
    // runHistory and runIndex are intentionally preserved: a hydrated
    // FSM should not look like it has zero past runs if the previous
    // session genuinely completed several. The orchestrator is free to
    // reset them via a fresh FSM if it wants the strict-clean variant.
    // maxState is monotonic: a backward jump (revisit) keeps it at the
    // furthest state ever reached so the stepper can still show those
    // steps as clickable history.

    this.cachedSnapshot = null;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      listener(snapshot);
    });
  }

  /**
   * Forgets everything this unit has been through: back to `S0`, with nothing
   * reached, no run and no tempo.
   *
   * Not `fastForwardTo('S0')`, which keeps `maxState` on purpose — a unit
   * started over still remembers how far it went. This is for the one caller
   * that means "discard everything", `LearningJourney.reset`.
   */
  forget(): void {
    this.state = 'S0';
    this.maxState = 'S0';
    this.positionIndex = 0;
    this.pace = null;
    this.runHistory = [];
    this.runIndex = 0;

    this.cachedSnapshot = null;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      listener(snapshot);
    });
  }

  /**
   * Routes an event to the current state. Unknown / out-of-state events
   * are silently ignored (the FSM does not throw; the UI is expected to
   * drive the right events for the current state).
   *
   * Renvoie `true` si quelque chose a bougé : l'orchestrateur ne persiste
   * et ne notifie que dans ce cas.
   */
  dispatch(event: PhraseEvent): boolean {
    const fingerprint = this.fingerprint();
    this.handleEvent(event);
    // maxState is monotonic: it tracks the furthest state ever reached
    // so the stepper UI can keep past steps clickable after a revisit.
    this.maxState = maxOfStates(this.maxState, this.state);
    const mutated = this.fingerprint() !== fingerprint;

    if (!mutated) {
      return false;
    }

    this.cachedSnapshot = null;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      listener(snapshot);
    });

    return true;
  }

  // ── Internal: event routing ────────────────────────────────────

  private handleEvent(event: PhraseEvent): void {
    switch (event.type) {
      case 'presentChordAcknowledged': {
        if (this.state === 'S0') {
          this.advanceFromS0();
        }

        return;
      }

      case 'positionConfirmed': {
        this.handlePositionConfirmed();

        return;
      }

      case 'practiceRunCompleted': {
        this.handlePracticeRun(event.outcome, event.pace);

        return;
      }

      case 'handSkipped': {
        this.handleHandSkipped();

        return;
      }

      case 'enterTime': {
        this.handleEnterTime(event.pace);

        return;
      }

      case 'leaveTime': {
        this.handleLeaveTime();

        return;
      }

      case 'rhythmRunCompleted': {
        this.handleRhythmRun(event.outcome);

        return;
      }

      case 'chainRunCompleted': {
        this.handleChainRun(event.outcome);

        return;
      }

      case 'runStarted': {
        this.handleRunStarted();

        return;
      }

      case 'stepBack': {
        this.handleStepBack();

        return;
      }

      case 'retry': {
        // No-op: retry is signalled by the caller dispatching a fresh
        // `runStarted` followed (later) by a `*RunCompleted`.
        return;
      }
    }
  }

  // ── Internal: S0 / grip / practice (linear) ────────────────────

  private advanceFromS0(): void {
    const hasLeft = this.phrase.notesByHand.left.length > 0;
    const hasRight = this.phrase.notesByHand.right.length > 0;

    if (!hasLeft && !hasRight) {
      this.state = 'DONE';

      return;
    }

    if (hasLeft) {
      this.state = this.entryStateForHand('left');
      this.positionIndex = 0;

      return;
    }

    // Right-hand-only phrase.
    this.state = this.entryStateForHand('right');
    this.positionIndex = 0;
  }

  /**
   * Where a hand starts: on its position step when it has one, otherwise
   * straight on practice.
   *
   * The position step only exists when the hand actually settles into few
   * enough shapes to name (see `hand-shapes.ts`). A hand that travels has no
   * shape to take, so inserting a step for it would assert an occupancy it
   * never held.
   */
  private entryStateForHand(hand: 'left' | 'right'): PhraseState {
    if (hand === 'left') {
      return hasShapeStep(this.phrase, 'left') ? 'S1a' : 'S1b';
    }

    return hasShapeStep(this.phrase, 'right') ? 'S2a' : 'S2b';
  }

  private handlePositionConfirmed(): void {
    if (this.state === 'S1a') {
      this.advancePosition('left', () => this.advanceFromS1a());

      return;
    }

    if (this.state === 'S2a') {
      this.advancePosition('right', () => this.advanceFromS2a());
    }
  }

  /**
   * The left hand's grip is taken. Its practice follows, when it has one.
   *
   * A hand that strikes a single attack in this unit has none: see
   * `hasPracticeStep`. The step it would have run is the key that was just
   * found, pressed once more, on a roll that does not move — and it ends on
   * a card offering to repeat it to memorise it.
   */
  private advanceFromS1a(): void {
    if (hasPracticeStep(this.phrase, 'left')) {
      this.state = 'S1b';

      return;
    }

    this.advanceFromS1b();
  }

  /** The same, for the right hand. See `advanceFromS1a`. */
  private advanceFromS2a(): void {
    if (hasPracticeStep(this.phrase, 'right')) {
      this.state = 'S2b';

      return;
    }

    this.advanceFromS2b();
  }

  /**
   * One press done. `positionIndex` counts **presses**, not notes: a shape
   * taken at three places is three, an orientation is one, and the whole
   * unit is capped at six. Never once per key.
   */
  private advancePosition(hand: 'left' | 'right', next: () => void): void {
    const total = shapeStepCount(phraseHandShapePlan(this.phrase, hand));

    this.positionIndex += 1;

    if (this.positionIndex >= total) {
      this.positionIndex = 0;
      next();
    }
  }

  /**
   * A run without rhythm came back.
   *
   * `pace` is only read on the way out of `S3`, and only when it is a
   * number: it is the tempo the run in time will open at, and the two
   * single-hand runs have no business setting it. One hand alone is roughly
   * half as dense as two, so a tempo measured on the left hand would open the
   * two-hand run at a speed the two hands have never held. A unit has one
   * tempo, and it is measured where both hands are playing.
   */
  private handlePracticeRun(outcome: RunOutcome, pace?: number): void {
    if (this.state !== 'S1b' && this.state !== 'S2b' && this.state !== 'S3') {
      return;
    }

    this.recordRun(outcome);

    if (outcome === 'failed') {
      return;
    }

    // The free régime of the two-hand step. A pass here is a repetition, not
    // a door: what it leaves behind is the tempo it was played at, and the
    // switch is what crosses. See the note on the class.
    if (this.state === this.freeRegimeState()) {
      if (pace !== undefined && pace > 0) {
        this.pace = pace;
      }

      return;
    }

    if (this.state === 'S1b') {
      this.advanceFromS1b();

      return;
    }

    if (this.state === 'S2b') {
      this.advanceFromS2b();
    }
  }

  /**
   * The player leaves a hand's steps — its grip or its run — without doing
   * them.
   *
   * Nothing is recorded: a pass that was not played is not a repetition, and
   * writing one in the history would count it in everything that reads it.
   * Lands where walking past the hand lands. See `handSkipTarget`.
   */
  private handleHandSkipped(): void {
    const target = this.handSkipTarget();

    if (target === null) {
      return;
    }

    this.state = target;
    this.positionIndex = 0;
  }

  /**
   * Where skipping the current hand leads, or `null` where it cannot be
   * skipped.
   *
   * Only from a hand's own steps. Never from the free régime of the timed
   * step: the switch is already the way on there, and a second one would
   * hand the metronome over without saying so.
   */
  private handSkipTarget(): PhraseState | null {
    if (this.state === this.freeRegimeState()) {
      return null;
    }

    if (this.state === 'S1a' || this.state === 'S1b') {
      return this.pastLeftHand();
    }

    if (this.state === 'S2a' || this.state === 'S2b') {
      return this.pastRightHand();
    }

    return null;
  }

  // ── Internal: the two régimes of the two-hand step ─────────────

  /**
   * The run **without rhythm** this unit's timed step is the other face of,
   * or `null` when it has none.
   *
   * `S3` on an ordinary two-hand unit. On a unit where one hand is silent
   * there is no both-hands run, and the free régime is that hand's own; the
   * same goes for a unit whose two hands strike a single instant together,
   * which has no `S3` either. A unit where no hand plays more than one attack
   * has no run without rhythm at all, and answers `null` — the switch is not
   * drawn, and `S4` keeps the plain « retour » of the stepper.
   *
   * Read in both directions, which is what makes the round trip land where
   * the player left: `enterTime` is only accepted from it, `leaveTime` only
   * goes to it.
   */
  private freeRegimeState(): PhraseState | null {
    for (const candidate of ['S3', 'S2b', 'S1b'] as const) {
      if (this.hasState(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  /**
   * The player takes the metronome.
   *
   * `pace` overrides what the free run last measured, and is ignored when it
   * is not a tempo: the step then opens on whatever the unit is remembered
   * by, which is what `openingRateInTime` is for.
   */
  private handleEnterTime(pace?: number): void {
    if (this.state !== this.freeRegimeState()) {
      return;
    }

    if (pace !== undefined && pace > 0) {
      this.pace = pace;
    }

    this.state = 'S4';
  }

  /** The player puts it down. The tempo it measured stays. */
  private handleLeaveTime(): void {
    if (this.state !== 'S4') {
      return;
    }

    const free = this.freeRegimeState();

    if (free === null) {
      return;
    }

    this.state = free;
    this.positionIndex = 0;
  }

  /**
   * Where the left hand's run leads when it is walked *past*.
   *
   * Two callers, and they are not the same act. `advanceFromS1a` skips a hand
   * that has no run to do, and must land somewhere; a validated pass only
   * gets here when `S1b` is not this unit's free régime, i.e. when the right
   * hand is still to come. The fall to `S4` is therefore the skip's, never a
   * pass's — a pass on the free régime stays where it is.
   */
  private advanceFromS1b(): void {
    this.state = this.pastLeftHand();
    this.positionIndex = 0;
  }

  private advanceFromS2b(): void {
    this.state = this.pastRightHand();
  }

  /**
   * The step after the left hand's: the right hand's first, or — on a
   * LH-only phrase — `S4`, skipping S2a/S2b *and* S3.
   *
   * One rule for the two ways past the hand, walking it and skipping it.
   */
  private pastLeftHand(): PhraseState {
    if (this.phrase.notesByHand.right.length > 0) {
      return this.entryStateForHand('right');
    }

    return 'S4';
  }

  /**
   * The step after the right hand's: `S3`, or `S4` on a RH-only phrase. So
   * does a unit whose two hands together strike once — there is no
   * coordination in a single instant.
   */
  private pastRightHand(): PhraseState {
    const hasLeft = this.phrase.notesByHand.left.length > 0;

    if (hasLeft && hasPracticeStep(this.phrase, 'both')) {
      return 'S3';
    }

    return 'S4';
  }

  // ── Internal: S4 ───────────────────────────────────────────────

  /**
   * A rhythm run came back.
   *
   * `failed` is recorded and does nothing else — the player stopped, the
   * view offers to start over, and no counter has an opinion about it. The
   * step is a place to repeat, not a test with attempts.
   */
  private handleRhythmRun(outcome: RunOutcome): void {
    if (this.state !== 'S4') {
      return;
    }

    this.recordRun(outcome);

    if (outcome === 'failed') {
      return;
    }

    if (!this.chains) {
      this.state = 'DONE';

      return;
    }

    this.enterRhythmOrChainState('S5');
  }

  // ── Internal: going back ───────────────────────────────────────

  /**
   * One sub-step back.
   *
   * Inside a grip step the sub-steps are the shapes the hand takes, so back
   * is one shape: the step asks for up to six of them, and losing all of them
   * to reach the one before is the same complaint one rung up. At the first
   * shape, or on any other step, back is the previous **state** the unit has.
   *
   * `maxState` is untouched — the whole point is that the player can walk
   * forward again through what they have already reached, and `dispatch`
   * takes care of not letting it move backwards.
   */
  private handleStepBack(): void {
    if (
      (this.state === 'S1a' || this.state === 'S2a') &&
      this.positionIndex > 0
    ) {
      this.positionIndex -= 1;

      return;
    }

    const previous = this.previousState();

    if (previous === null) {
      return;
    }

    this.state = previous;
    this.positionIndex = 0;
  }

  /**
   * The sub-step behind the current one, or `null` when there is none.
   *
   * Walks the canonical order backwards and takes the first state this unit
   * actually has — which is what makes it right on a unit that skips steps: a
   * right-hand-only unit goes from its practice straight back to the
   * presentation, because nothing sits between them for that unit.
   *
   * `DONE` has none. Its panel is not a step to be walked back out of: it
   * offers its own ways into the unit, and « retour » there would land on the
   * chaining step of a unit the player has just finished.
   */
  private previousState(): PhraseState | null {
    if (this.state === 'S0' || this.state === 'DONE') {
      return null;
    }

    for (let i = stateIndex(this.state) - 1; i >= 0; i -= 1) {
      const candidate = STATE_ORDER[i];

      if (this.hasState(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  /**
   * Whether this unit has a given step at all.
   *
   * The one table of the skipping rules stated in the class header, read by
   * everything that needs them: where a jump lands (`coerceToLegalState`) and
   * what sits behind the current step (`previousState`). They used to be two
   * hand-written walks of the same conditions, which is how a rule gets fixed
   * in one of them and not the other.
   */
  private hasState(state: PhraseState): boolean {
    const hasLeft = this.phrase.notesByHand.left.length > 0;
    const hasRight = this.phrase.notesByHand.right.length > 0;

    switch (state) {
      case 'S0':
      case 'DONE':
        return true;
      case 'S1a':
        return hasLeft && hasShapeStep(this.phrase, 'left');
      case 'S1b':
        return hasLeft && hasPracticeStep(this.phrase, 'left');
      case 'S2a':
        return hasRight && hasShapeStep(this.phrase, 'right');
      case 'S2b':
        return hasRight && hasPracticeStep(this.phrase, 'right');
      case 'S3':
        return hasLeft && hasRight && hasPracticeStep(this.phrase, 'both');
      case 'S4':
        return hasLeft || hasRight;
      case 'S5':
        return this.chains && (hasLeft || hasRight);
    }
  }

  // ── Internal: S5 ───────────────────────────────────────────────

  private handleChainRun(outcome: RunOutcome): void {
    if (this.state !== 'S5') {
      return;
    }

    this.recordRun(outcome);

    if (outcome === 'failed') {
      return;
    }

    this.state = 'DONE';
  }

  // ── Internal: run lifecycle (shared S4 / S5) ───────────────────

  private handleRunStarted(): void {
    if (this.state !== 'S4' && this.state !== 'S5') {
      return;
    }

    this.runIndex += 1;
  }

  // ── Internal: state-entry helpers ──────────────────────────────

  /** Enters a state that plays a range in time (S4 / S5). */
  private enterRhythmOrChainState(next: 'S4' | 'S5'): void {
    this.state = next;
  }

  // ── Internal: history & snapshot ───────────────────────────────

  private recordRun(outcome: RunOutcome): void {
    this.runHistory.push({
      state: this.state,
      outcome,
    });
  }

  /**
   * Stable string fingerprint of all observable state, used to detect
   * whether a dispatch produced an effective mutation. Cheaper than
   * structural comparison and avoids the boilerplate of tracking each
   * field's "previous" value.
   */
  private fingerprint(): string {
    return [
      this.state,
      this.positionIndex,
      this.pace,
      this.runHistory.length,
      this.runIndex,
    ].join('|');
  }

  /**
   * Coerces a jump target to a state this unit can legitimately be in: the
   * first step it **has**, at or after the target.
   *
   * One rule where there used to be nine special cases, and the same rule
   * every skip in this class already follows — a step the unit does not have
   * is walked past, forwards. It reads the same table `previousState` reads
   * backwards (`hasState`), so the two can no longer disagree about whether
   * a unit has a step.
   *
   * Two consequences worth naming, both unchanged from the nine cases:
   * a unit with no notes lands on `DONE`, since it has nothing between `S0`
   * and there; and the first unit of a piece, which has no seam to play,
   * turns a jump to the chaining step into `DONE`.
   */
  private coerceToLegalState(target: PhraseState): PhraseState {
    for (let i = stateIndex(target); i < STATE_ORDER.length; i += 1) {
      const candidate = STATE_ORDER[i];

      if (this.hasState(candidate)) {
        return candidate;
      }
    }

    return 'DONE';
  }

  private buildSnapshot(): PhraseFSMSnapshot {
    const frozenHistory: RunHistory = this.runHistory.slice();
    const free = this.freeRegimeState();

    return {
      state: this.state,
      maxState: this.maxState,
      positionIndex: this.positionIndex,
      pace: this.pace,
      canStepBack:
        this.positionIndex > 0 || this.previousState() !== null,
      canEnterTime: free !== null && this.state === free,
      canLeaveTime: this.state === 'S4' && free !== null,
      handSkipTarget: this.handSkipTarget(),
      chains: this.chains,
      runHistory: frozenHistory,
      isDone: this.state === 'DONE',
    };
  }
}
